import { useState, useEffect } from 'react';
import {
  useListCarriers,
  useCreateCarrier,
  useUpdateCarrier,
  useCreateCarrierContact,
  useUpdateCarrierContact,
  useDeleteCarrierContact,
} from '../../features/carriers/api';
import { CarrierContact, CarrierContactType } from '../../features/carriers/types';
import { formatPhone } from '../../utils/formatters';
import { toast } from 'react-toastify';
import AddressAutocomplete, {
  ParsedAddress,
} from '../../components/AddressAutocomplete/AddressAutocomplete';
import { CarrierDocumentsPanel } from '../../components/CarrierDocumentsPanel/CarrierDocumentsPanel';

interface CarrierDrawerProps {
  carrierId: string | null;
  onClose: () => void;
}

interface Addr {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const EMPTY_ADDR: Addr = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
};

// One optional address block (billing or garage). Line 1 autocompletes and
// fills the rest; every field stays hand-editable.
function AddressBlock({
  title,
  value,
  onChange,
}: {
  title: string;
  value: Addr;
  onChange: (next: Addr) => void;
}) {
  const set = (key: keyof Addr) => (v: string) => onChange({ ...value, [key]: v });

  return (
    <div className="drawer-address-block">
      <div className="drawer-address-block__label">
        {title} <span className="drawer-address-block__optional">optional</span>
      </div>

      <div className="form-control">
        <label>Address line 1</label>
        <AddressAutocomplete
          value={value.line1}
          onChange={(e) => set('line1')(e.target.value)}
          onPlaceSelected={(p: ParsedAddress) =>
            onChange({
              ...value,
              line1: p.line1 ?? value.line1,
              city: p.city ?? value.city,
              state: p.state ?? value.state,
              postalCode: p.postalCode ?? value.postalCode,
              country: p.country ?? value.country,
            })
          }
          placeholder="Start typing an address…"
        />
      </div>

      <div className="form-control">
        <label>Line 2</label>
        <input value={value.line2} onChange={(e) => set('line2')(e.target.value)} />
      </div>

      <div className="drawer-info-grid">
        <div className="form-control">
          <label>City</label>
          <input value={value.city} onChange={(e) => set('city')(e.target.value)} />
        </div>
        <div className="form-control">
          <label>State</label>
          <input value={value.state} onChange={(e) => set('state')(e.target.value)} />
        </div>
        <div className="form-control">
          <label>Postal code</label>
          <input value={value.postalCode} onChange={(e) => set('postalCode')(e.target.value)} />
        </div>
        <div className="form-control">
          <label>Country</label>
          <input value={value.country} onChange={(e) => set('country')(e.target.value)} />
        </div>
      </div>
    </div>
  );
}

export function CarrierDrawer({ carrierId, onClose }: CarrierDrawerProps) {
  const { data } = useListCarriers();
  const createCarrier = useCreateCarrier();
  const updateCarrier = useUpdateCarrier();

  // After creating a new carrier, keep the drawer open so contacts can be added immediately
  const [createdId, setCreatedId] = useState<string | null>(null);
  const effectiveId = carrierId ?? createdId;

  const carrier = effectiveId ? data?.carriers?.find((c) => c.id === effectiveId) : null;
  const isEdit = !!carrierId || !!createdId;

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [dotNumber, setDotNumber] = useState('');
  const [mcNumber, setMcNumber] = useState('');
  const [vehicleNotes, setVehicleNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [truckNumbers, setTruckNumbers] = useState<string[]>([]);
  const [truckInput, setTruckInput] = useState('');
  const [billing, setBilling] = useState<Addr>(EMPTY_ADDR);
  const [garage, setGarage] = useState<Addr>(EMPTY_ADDR);

  // Load carrier data when editing
  useEffect(() => {
    if (carrier) {
      setCompanyName(carrier.companyName || '');
      setPhone(carrier.phone || '');
      setDotNumber(carrier.dotNumber || '');
      setMcNumber(carrier.mcNumber || '');
      setVehicleNotes(carrier.vehicleNotes || '');
      setNotes(carrier.notes || '');
      setTruckNumbers(carrier.truckNumbers ?? []);
      setTruckInput('');
      setBilling({
        line1: carrier.billingLine1 || '',
        line2: carrier.billingLine2 || '',
        city: carrier.billingCity || '',
        state: carrier.billingState || '',
        postalCode: carrier.billingPostalCode || '',
        country: carrier.billingCountry || '',
      });
      setGarage({
        line1: carrier.garageLine1 || '',
        line2: carrier.garageLine2 || '',
        city: carrier.garageCity || '',
        state: carrier.garageState || '',
        postalCode: carrier.garagePostalCode || '',
        country: carrier.garageCountry || '',
      });
    } else {
      // Reset for create
      setCompanyName('');
      setPhone('');
      setDotNumber('');
      setMcNumber('');
      setVehicleNotes('');
      setNotes('');
      setTruckNumbers([]);
      setTruckInput('');
      setBilling(EMPTY_ADDR);
      setGarage(EMPTY_ADDR);
    }
  }, [carrier]);

  const handleSave = async () => {
    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!phone.trim()) {
      toast.error('Phone is required');
      return;
    }

    const payload = {
      companyName: companyName.trim(),
      phone: phone.trim(),
      dotNumber: dotNumber.trim() || undefined,
      mcNumber: mcNumber.trim() || undefined,
      vehicleNotes: vehicleNotes.trim() || undefined,
      notes: notes.trim() || undefined,
      truckNumbers,
      billingLine1: billing.line1.trim() || undefined,
      billingLine2: billing.line2.trim() || undefined,
      billingCity: billing.city.trim() || undefined,
      billingState: billing.state.trim() || undefined,
      billingPostalCode: billing.postalCode.trim() || undefined,
      billingCountry: billing.country.trim() || undefined,
      garageLine1: garage.line1.trim() || undefined,
      garageLine2: garage.line2.trim() || undefined,
      garageCity: garage.city.trim() || undefined,
      garageState: garage.state.trim() || undefined,
      garagePostalCode: garage.postalCode.trim() || undefined,
      garageCountry: garage.country.trim() || undefined,
    };

    try {
      if (isEdit && effectiveId && !createdId) {
        await updateCarrier.mutateAsync({ carrierId: effectiveId, ...payload });
        toast.success('Carrier updated');
        onClose();
      } else if (!isEdit) {
        const result = await createCarrier.mutateAsync(payload);
        toast.success('Carrier created. Add contacts below.');
        setCreatedId(result.carrier.id);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to save carrier');
    }
  };

  const canSave =
    companyName.trim() && phone.trim() && !createCarrier.isPending && !updateCarrier.isPending;

  return (
    <div className="drawer">
      <div className="drawer__panel">
        <div className="drawer__header">
          <h3>{createdId ? 'Carrier Created' : isEdit ? 'Edit Carrier' : 'Add Carrier'}</h3>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="drawer__body">
          {/* Company Info */}
          <div className="drawer-info-grid">
            <div className="form-control">
              <label>Company name *</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="ABC Trucking"
              />
            </div>

            <div className="form-control">
              <label>Phone *</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="555-123-4567"
              />
            </div>

            <div className="form-control">
              <label>DOT Number</label>
              <input
                value={dotNumber}
                onChange={(e) => setDotNumber(e.target.value)}
                placeholder="12345"
              />
            </div>

            <div className="form-control">
              <label>MC Number</label>
              <input
                value={mcNumber}
                onChange={(e) => setMcNumber(e.target.value)}
                placeholder="67890"
              />
            </div>
          </div>

          {/* ── Billing & garage addresses (optional) ── */}
          <AddressBlock title="Billing Address" value={billing} onChange={setBilling} />
          <AddressBlock title="Garage Address" value={garage} onChange={setGarage} />

          {/* ── Truck Numbers ── */}
          <div className="form-control drawer-tag-field">
            <label>Truck numbers</label>
            {truckNumbers.length > 0 && (
              <div className="drawer-tag-chips">
                {truckNumbers.map((truck) => (
                  <span key={truck} className="drawer-chip drawer-chip--truck">
                    {truck}
                    <button
                      type="button"
                      className="drawer-chip__remove"
                      onClick={() => setTruckNumbers(truckNumbers.filter((t) => t !== truck))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="drawer-tag-input-row">
              <input
                value={truckInput}
                onChange={(e) => setTruckInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = truckInput.trim();
                    if (val && !truckNumbers.includes(val)) {
                      setTruckNumbers([...truckNumbers, val]);
                      setTruckInput('');
                    }
                  }
                }}
                placeholder="TR-001"
              />
              {truckInput.trim() && (
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => {
                    const val = truckInput.trim();
                    if (val && !truckNumbers.includes(val)) {
                      setTruckNumbers([...truckNumbers, val]);
                      setTruckInput('');
                    }
                  }}
                >
                  Add
                </button>
              )}
            </div>
            <div className="muted drawer-tag-hint">
              Selectable when assigning loads. Press Enter or click Add.
            </div>
          </div>

          {/* Vehicle Notes */}
          <div className="form-control drawer-notes-field">
            <label>Vehicle notes</label>
            <textarea
              value={vehicleNotes}
              onChange={(e) => setVehicleNotes(e.target.value)}
              placeholder="Fleet info, vehicle types, capacity…"
              rows={3}
            />
          </div>

          {/* General Notes */}
          <div className="form-control drawer-notes-field">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes about this carrier…"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="actions drawer-actions">
            {!createdId && (
              <button className="btn btn--primary" onClick={handleSave} disabled={!canSave}>
                {createCarrier.isPending || updateCarrier.isPending
                  ? 'Saving…'
                  : isEdit
                    ? 'Update'
                    : 'Create'}
              </button>
            )}
            <button
              className="btn btn--ghost"
              onClick={onClose}
              disabled={createCarrier.isPending || updateCarrier.isPending}
            >
              {createdId ? 'Done' : 'Cancel'}
            </button>
          </div>

          {/* ── Contacts — shown when editing or after first-time create ── */}
          {effectiveId && <ContactsSection carrierId={effectiveId} />}

          {/* ── Documents (COI, W-9, …) — needs a saved carrier to attach to ── */}
          {effectiveId && <CarrierDocumentsPanel carrierId={effectiveId} />}
        </div>
      </div>

      <div className="drawer__backdrop" onClick={onClose} />
    </div>
  );
}

// ─── ContactsSection ──────────────────────────────────────────────────────────

const EMPTY_CONTACT = {
  type: 'DRIVER' as CarrierContactType,
  name: '',
  phone: '',
  email: '',
  notes: '',
};

function ContactsSection({ carrierId }: { carrierId: string }) {
  // Read contacts directly from the query so invalidation always reflects here
  const { data } = useListCarriers();
  const contacts: CarrierContact[] =
    data?.carriers?.find((c) => c.id === carrierId)?.contacts ?? [];

  const createContact = useCreateCarrierContact(carrierId);
  const updateContact = useUpdateCarrierContact(carrierId);
  const deleteContact = useDeleteCarrierContact(carrierId);

  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState(EMPTY_CONTACT);

  const beginNew = () => {
    setForm(EMPTY_CONTACT);
    setEditingId('new');
  };

  const beginEdit = (c: CarrierContact) => {
    setForm({
      type: c.type,
      name: c.name,
      phone: c.phone ?? '',
      email: c.email ?? '',
      notes: c.notes ?? '',
    });
    setEditingId(c.id);
  };

  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY_CONTACT);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.warn('Name is required');
    try {
      if (editingId === 'new') {
        await createContact.mutateAsync({ ...form, name: form.name.trim() });
        toast.success('Contact added');
      } else if (editingId) {
        await updateContact.mutateAsync({ contactId: editingId, ...form, name: form.name.trim() });
        toast.success('Contact updated');
      }
      cancel();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to save contact');
    }
  };

  const handleDelete = async (c: CarrierContact) => {
    if (!window.confirm(`Remove contact "${c.name}"?`)) return;
    try {
      await deleteContact.mutateAsync(c.id);
      toast.success('Contact removed');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to remove contact');
    }
  };

  const isSaving = createContact.isPending || updateContact.isPending;
  const drivers = contacts.filter((c) => c.type === 'DRIVER');
  const dispatchers = contacts.filter((c) => c.type === 'DISPATCHER');

  return (
    <div className="contacts-section">
      <div className="contacts-section__header">
        <span className="contacts-section__title">Contacts</span>
        {editingId === null && (
          <button type="button" className="btn btn--primary btn--sm" onClick={beginNew}>
            + Add Contact
          </button>
        )}
      </div>

      {editingId !== null && (
        <div className="contact-form">
          <div className="contact-form__grid">
            <div className="form-control">
              <label>Type</label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as CarrierContactType }))
                }
              >
                <option value="DRIVER">Driver</option>
                <option value="DISPATCHER">Dispatcher</option>
              </select>
            </div>
            <div className="form-control">
              <label>Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div className="form-control">
              <label>Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="555-123-4567"
              />
            </div>
            <div className="form-control">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="driver@carrier.com"
              />
            </div>
            <div className="form-control" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <input
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes…"
              />
            </div>
          </div>
          <div className="actions" style={{ gap: 8, marginTop: 8 }}>
            <button className="btn btn--primary btn--sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : editingId === 'new' ? 'Add' : 'Update'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={cancel} disabled={isSaving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {[
        { label: 'Drivers', list: drivers },
        { label: 'Dispatchers', list: dispatchers },
      ].map(
        ({ label, list }) =>
          list.length > 0 && (
            <div key={label} className="contact-group">
              <div className="contact-group__label">{label}</div>
              {list.map((c) => (
                <div key={c.id} className="contact-card">
                  <div className="contact-card__info">
                    <div className="contact-card__name">{c.name}</div>
                    {c.phone && <div className="contact-card__detail">{formatPhone(c.phone)}</div>}
                    {c.email && <div className="contact-card__detail">{c.email}</div>}
                    {c.notes && <div className="contact-card__notes muted tiny">{c.notes}</div>}
                  </div>
                  <div className="contact-card__actions">
                    <button className="btn btn--ghost btn--xs" onClick={() => beginEdit(c)}>
                      Edit
                    </button>
                    <button
                      className="btn btn--ghost btn--xs"
                      onClick={() => handleDelete(c)}
                      disabled={deleteContact.isPending}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
      )}

      {contacts.length === 0 && editingId === null && (
        <div className="muted" style={{ fontSize: 13 }}>
          No contacts yet. Add drivers or dispatchers above.
        </div>
      )}
    </div>
  );
}
